import { type FC, useEffect } from 'react';
import { Collapse, Form, Radio, Spin } from 'antd';
import type { FormInstance } from 'antd';

import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import ConfigDrivenFormFields from '../../components/ConfigDrivenFormFields';
import type { UseDimOptionsHook } from '../../components/DimSelect';
import {
    TPO_EVENTS_DETAILS_SECTION,
    TPO_EVENTS_PROMO_DETAILS_SECTION,
    TPO_EVENTS_VIEW_TOGGLE,
    tpoOptionTestId,
} from '../../config/testIds';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import useEventAttributeFilters from './hooks/useEventAttributeFilters';
import EventPromoSection from './EventPromoSection';
import type { PromoEntry } from './hooks/useGetEventData';
import type { UseRuleBoundFormResult } from '../../rules/store/useRuleBoundForm';

const PANEL_STYLE = { backgroundColor: '#FFF' };
const COLLAPSE_DEFAULT_KEYS: string[] = ['1'];
const SHOW_BLOCK_STYLE = { display: 'block' } as const;
const HIDE_BLOCK_STYLE = { display: 'none' } as const;

type EventActiveView = 'event-details' | 'promo-details';

interface EventsSectionProps {
    form: FormInstance;
    isEventSaved?: boolean;
    /** Rule-driven disable for the Add-promo affordance (OR'd with `!isEventSaved`). */
    addPromoDisabled?: boolean;
    onAddPromo?: () => void;
    onDeletePromo?: (promoId: string) => void;
    isEditMode?: boolean;
    fetchedFormValues?: Record<string, any> | null;
    /** True during the initial fetch AND any background refetch (e.g. post-Update). */
    isEventDataLoading?: boolean;
    eventPromos?: PromoEntry[];
    activeEventView: EventActiveView;
    onActiveEventViewChange: (view: EventActiveView) => void;
    derivedStates: UseRuleBoundFormResult['derivedStates'];
    onValuesChange: UseRuleBoundFormResult['onValuesChange'];
    syncToStore: UseRuleBoundFormResult['syncToStore'];
}

const useEventDimOptions: UseDimOptionsHook = (params) =>
    useEventAttributeFilters({
        dimension: params.dimension,
        attributeType: params.attributeType,
        lowestAttribute: params.lowestAttribute ?? false,
        filters: (params.filters ?? {}) as Record<string, unknown>,
        selectedDimension: params.selectedDimension ?? {
            value: '',
            dimension: params.dimension,
            attributeType: params.attributeType,
        },
        searchString: params.searchString ?? '',
        enabled: params.enabled,
    });

const EventsSection: FC<EventsSectionProps> = ({
    form: eventForm,
    isEventSaved,
    addPromoDisabled,
    onAddPromo,
    onDeletePromo,
    isEditMode,
    fetchedFormValues: fetchedEventFormValues,
    isEventDataLoading,
    eventPromos,
    activeEventView,
    onActiveEventViewChange,
    derivedStates,
    onValuesChange: ruleOnValuesChange,
    syncToStore,
}) => {
    const tpoGenConfig = useTpoGenConfig();
    const { currentPlanDetails } = usePlanDetailsContext();
    const planYear = currentPlanDetails?.plan_year;
    const isEventDetailsView = activeEventView === 'event-details';
    const isPromoDetailsView = activeEventView === 'promo-details';

    useEffect(() => {
        if (isEditMode && fetchedEventFormValues) {
            // Clear any leftover values from a prior (create/other-event) session on this
            // shared form instance, so the form reflects only the fetched server state.
            // The GET response omits some fields (e.g. workflow_status), so a plain merge
            // would keep stale values and mark the form spuriously dirty.
            eventForm.resetFields();
            eventForm.setFieldsValue(fetchedEventFormValues);
            syncToStore(fetchedEventFormValues, 'hydrate');
        }
    }, [isEditMode, fetchedEventFormValues, eventForm, syncToStore]);

    return (
        <div className="relative">
            {isEditMode && isEventDataLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                    <Spin size="default" />
                </div>
            )}
            <div className="flex flex-row justify-between gap-x-4">
                <p className="text-base font-medium">
                    {isEventDetailsView
                        ? tpoGenConfig.labels.events.eventDetails
                        : tpoGenConfig.labels.events.productPortfolio}
                </p>
                <Radio.Group
                    value={activeEventView}
                    onChange={(e) => onActiveEventViewChange(e.target.value)}
                    data-testid={TPO_EVENTS_VIEW_TOGGLE}
                >
                    <Radio.Button
                        value="event-details"
                        data-testid={tpoOptionTestId(TPO_EVENTS_VIEW_TOGGLE, 'event-details')}
                        data-state-active={isEventDetailsView ? 'true' : undefined}
                    >
                        <p className={`text-sm ${isEventDetailsView ? 'font-medium' : ''}`}>
                            {tpoGenConfig.labels.events.eventDetails}
                        </p>
                    </Radio.Button>
                    <Radio.Button
                        value="promo-details"
                        data-testid={tpoOptionTestId(TPO_EVENTS_VIEW_TOGGLE, 'promo-details')}
                        data-state-active={isPromoDetailsView ? 'true' : undefined}
                    >
                        <p className={`text-sm ${isPromoDetailsView ? 'font-medium' : ''}`}>
                            {tpoGenConfig.labels.events.productPortfolio}
                        </p>
                    </Radio.Button>
                </Radio.Group>
            </div>
            <Form
                form={eventForm}
                name="event-details"
                layout="vertical"
                onValuesChange={(changed) => ruleOnValuesChange(changed)}
            >
                <div className="my-4">
                    <div
                        style={isEventDetailsView ? SHOW_BLOCK_STYLE : HIDE_BLOCK_STYLE}
                        data-testid={TPO_EVENTS_DETAILS_SECTION}
                    >
                        <Collapse
                            expandIconPosition="end"
                            defaultActiveKey={COLLAPSE_DEFAULT_KEYS}
                            style={PANEL_STYLE}
                        >
                            <Collapse.Panel
                                header={
                                    <p className="text-base font-semibold">
                                        {isEditMode
                                            ? tpoGenConfig.labels.events.editEventDetails
                                            : tpoGenConfig.labels.events.addEventDetails}
                                    </p>
                                }
                                key="1"
                            >
                                <ConfigDrivenFormFields
                                    scope="event"
                                    fields={tpoGenConfig.eventDetailsFields}
                                    isEditMode={isEditMode}
                                    useDimOptions={useEventDimOptions}
                                    derivedStates={derivedStates}
                                    planYear={planYear}
                                />
                            </Collapse.Panel>
                        </Collapse>
                    </div>
                    <div
                        style={isPromoDetailsView ? SHOW_BLOCK_STYLE : HIDE_BLOCK_STYLE}
                        data-testid={TPO_EVENTS_PROMO_DETAILS_SECTION}
                    >
                        <Collapse
                            expandIconPosition="end"
                            defaultActiveKey={COLLAPSE_DEFAULT_KEYS}
                            style={PANEL_STYLE}
                        >
                            <Collapse.Panel
                                header={
                                    <p className="text-base font-semibold">
                                        {isEditMode
                                            ? tpoGenConfig.labels.events.editProductDetails
                                            : tpoGenConfig.labels.events.addProductDetails}
                                    </p>
                                }
                                key="1"
                            >
                                <ConfigDrivenFormFields
                                    scope="event"
                                    fields={tpoGenConfig.promoDetailsFields}
                                    isEditMode={isEditMode}
                                    useDimOptions={useEventDimOptions}
                                />
                            </Collapse.Panel>
                        </Collapse>
                    </div>
                </div>
            </Form>
            <EventPromoSection
                isEventSaved={isEventSaved}
                addPromoDisabled={addPromoDisabled}
                promos={eventPromos}
                onAddPromo={onAddPromo}
                onDeletePromo={onDeletePromo}
            />
        </div>
    );
};

EventsSection.displayName = 'EventsSection';
export default EventsSection;
